import {
  Box,
  Button,
  Flex,
  Image,
  SimpleGrid,
  Spinner,
  Text,
} from '@chakra-ui/react';
import rhLogo from '@assets/rh_logo.svg';
import { Role } from '@texas/api/endpoints/userApi';
import { Icons } from '@texas/components/shared/Icons';
import { RoleRestrict } from '@texas/components/shared/RoleRestrict';
import { useSubscriber, usePublisher } from '@texas/messageBus/useMessageBus';
import { OpenGlobalSearch, ServerStatePoll } from '@texas/messageBus/messages';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GlobalNavigationItem,
  GlobalUtilityNavigationItem,
} from './GlobalNavigationItem';
import { NavigationItemBadge } from './NavigationItemBadge';
import { NavigationProfile } from '../profile/NavigationProfile';
import { FeatureGuard } from '@texas/components/shared/featureGuard/FeatureGuard';
import { CompactGlobalUtilityNavigation } from './CompactGlobalUtilityNavigation';

interface GlobalNavigationProps {
  activeState: string;
  isRioOpen: boolean;
  onRioToggle: () => void;
}

function isStateActive(activeState: string, route: string) {
  return activeState === route || activeState.startsWith(`${route}.`);
}

const expandedUtilityNavigationStyles = {
  '@media screen and (max-height: 900px)': {
    display: 'none',
  },
};

export function GlobalNavigation({
  activeState,
  isRioOpen,
  onRioToggle,
}: GlobalNavigationProps) {
  const { t } = useTranslation();
  const { publish } = usePublisher();
  const [tasksCount, setTasksCount] = useState(0);
  const [hasPendingJobs, setHasPendingJobs] = useState(false);

  useSubscriber(ServerStatePoll, (data) => {
    setTasksCount(data.data.unconfirmedTasksCount);
    setHasPendingJobs(data.data.runningJobs.length > 0);
  });

  return (
    <Flex
      as="nav"
      aria-label={t('navigation.application')}
      direction="column"
      alignItems="center"
      flexShrink={0}
      w="104px"
      h="100dvh"
      pl={4}
      pr={5}
      pt={5}
      pb={4}
      bg="navigation.globalBackground"
      color="white"
      overflowX="hidden"
      overflowY="auto"
    >
      <Image src={rhLogo} alt="Rudholm" flexShrink={0} w={12} />

      <Flex direction="column" gap={1.25} w={20} pt={9}>
        <GlobalNavigationItem
          label={t('navigation.dashboard')}
          route="customers"
          active={isStateActive(activeState, 'customers')}
          icon={<Icons.Grid boxSize={5} />}
        />
        <RoleRestrict
          allow={[Role.SystemAdministrator, Role.InternalAdministrator]}
        >
          <GlobalNavigationItem
            label={t('navigation.articles')}
            route="searcharticles"
            active={
              isStateActive(activeState, 'searcharticles') ||
              isStateActive(activeState, 'article') ||
              isStateActive(activeState, 'variant')
            }
            icon={<Icons.FileOutline boxSize={5} />}
          />
          <GlobalNavigationItem
            label={t('navigation.brands')}
            route="customerDirectory"
            active={
              isStateActive(activeState, 'customerDirectory') ||
              isStateActive(activeState, 'customer') ||
              isStateActive(activeState, 'concept')
            }
            icon={<Icons.AccountCircle boxSize={5} />}
          />
          <RoleRestrict allow={[Role.SystemAdministrator]}>
            <GlobalNavigationItem
              label={t('navigation.users')}
              route="users"
              active={isStateActive(activeState, 'users')}
              icon={<Icons.Users boxSize={5} />}
            />
          </RoleRestrict>
          <GlobalNavigationItem
            label={t('navigation.suppliers')}
            route="suppliers"
            active={isStateActive(activeState, 'suppliers')}
            icon={<Icons.Factory boxSize={5} />}
          />
          <GlobalNavigationItem
            label={t('vdc.title')}
            route="vdcpricing"
            active={isStateActive(activeState, 'vdcpricing')}
            icon={<Icons.CashMultiple boxSize={5} />}
          />
          <GlobalNavigationItem
            label={t('navigation.shipments')}
            route="shipments"
            active={isStateActive(activeState, 'shipments')}
            icon={<Icons.Truck boxSize={5} />}
          />
          <FeatureGuard hideBadge={true}>
            <Button
              background={isRioOpen ? 'texas.burntSienna.500' : 'transparent'}
              color={isRioOpen ? 'white' : 'navigation.globalMuted'}
              variant="globalNavigation"
              _hover={{
                background: isRioOpen
                  ? 'texas.burntSienna.500'
                  : 'whiteAlpha.100',
                color: 'white',
              }}
              onClick={onRioToggle}
            >
              <Icons.Chat boxSize={5} />
              <Text fontSize="12px" fontWeight="normal" lineHeight="14px">
                {t('navigation.rio')}
              </Text>
            </Button>
          </FeatureGuard>
          <Button
            variant="globalNavigation"
            onClick={() => publish(new OpenGlobalSearch())}
          >
            <Icons.Search boxSize={5} />
            <Text fontSize="12px" fontWeight="normal" lineHeight="14px">
              {t('navigation.search')}
            </Text>
          </Button>
        </RoleRestrict>
      </Flex>

      <Box flex={1} minH={4} />
      <SimpleGrid
        columns={2}
        spacing={1}
        w={20}
        pt={3}
        borderTopWidth="1px"
        borderColor="whiteAlpha.200"
        sx={expandedUtilityNavigationStyles}
      >
        <GlobalUtilityNavigationItem
          label={t('navigation.tags')}
          route="tags"
          active={isStateActive(activeState, 'tags')}
          icon={<Icons.Tag boxSize={4} />}
        />
        <Box position="relative">
          <GlobalUtilityNavigationItem
            label={t('navigation.tasks')}
            route="tasks"
            active={isStateActive(activeState, 'tasks')}
            icon={<Icons.CalendarCheck boxSize={4} />}
          />
          <NavigationItemBadge count={tasksCount} />
        </Box>
        <GlobalUtilityNavigationItem
          label={t('navigation.jobs')}
          route="jobs"
          active={isStateActive(activeState, 'jobs')}
          icon={
            hasPendingJobs ? (
              <Spinner boxSize={4} speed="0.7s" thickness="2px" />
            ) : (
              <Icons.History boxSize={4} />
            )
          }
        />
        <RoleRestrict
          allow={[Role.SystemAdministrator, Role.InternalAdministrator]}
        >
          <GlobalUtilityNavigationItem
            label={t('navigation.configurationShort')}
            route="configuration"
            active={isStateActive(activeState, 'configuration')}
            icon={<Icons.Cog boxSize={4} />}
          />
        </RoleRestrict>
        <RoleRestrict allow={[Role.SystemAdministrator]}>
          <GlobalUtilityNavigationItem
            label={t('navigation.archive')}
            route="archive"
            active={isStateActive(activeState, 'archive')}
            icon={<Icons.Archive boxSize={4} />}
          />
        </RoleRestrict>
      </SimpleGrid>
      <CompactGlobalUtilityNavigation
        activeState={activeState}
        hasPendingJobs={hasPendingJobs}
        tasksCount={tasksCount}
      />
      <Box pt={3}>
        <NavigationProfile />
      </Box>
    </Flex>
  );
}
